/**
 * Slowly migrating client schema to be generalizable
 */

import { type ZodTypeAny, z } from "zod";

import type { PricingMetadata } from "@frontend/components/pricingExplainability/pricingExplainabilityCopy";
import {
  compareAsc,
  format,
  isAfter,
  isBefore,
  isFuture,
  isToday,
  isValid,
  parse,
} from "date-fns";
import {
  EmergencyContactRelationship,
  Ethnicity,
  InsuranceAccountStatus,
  InsurancesStatusEnum,
  LanguageCode,
  MaritalStatus,
  Race,
} from "../graphql/generated-gql";

const transformStringToDate = (val: string | null | undefined) => {
  if (!val) return null;

  return parse(val.slice(0, 10), "yyyy-MM-dd", new Date());
};

export const validateDateIsTodayOrFuture = (
  date: string | null | undefined,
) => {
  if (!date) return true;

  const transformedDate = transformStringToDate(date);

  if (!transformedDate) return true;

  return isToday(transformedDate) || isFuture(transformedDate);
};

export const PaymentMethodType = ["CASH", "CARD", "CHECK", "POS"] as const;

export const TransactionTypes = ["PAYMENT", "REFUND", "TOTAL_REFUND"] as const;

export const PaymentMethodTypes = ["CASH", "CHECK", "CARD", "POS"] as const;

export const CHARGETYPE = ["FEE", "POOL"] as const;

export type SORTTYPE = "ASC" | "DESC";

export const paymentmethodIdRegex = new RegExp(/^pm_/);

export const TransactionStatuses = [
  "VOIDED",
  "CANCELLED",
  "REFUNDED",
  "COMPLETED",
  "PENDING",
  "REPAID",
  "CHECKCASHING",
  "RECREATED",
] as const;

export const EncounterStatuses = [
  "active",
  "canceled",
  "deleted",
  "replaced",
] as const;

/**
 * Standardized transaction statuses as enums
 */
export const transactionStatus = z.enum(TransactionStatuses);
export type transactionStatusType = z.infer<typeof transactionStatus>;

export const encounterStatus = z.enum(EncounterStatuses);
export type encounterStatusType = z.infer<typeof encounterStatus>;

export const transactionType = z.enum(TransactionTypes);
export type transactionTypeType = z.infer<typeof transactionType>;

export const paymentmethod = z.enum(PaymentMethodType);
export type paymentmethodType = z.infer<typeof paymentmethod>;

export const stateInitials = [
  "AL",
  "AK",
  "AS",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "DC",
  "FM",
  "FL",
  "GA",
  "GU",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MH",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "MP",
  "OH",
  "OK",
  "OR",
  "PW",
  "PA",
  "PR",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VI",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
  "N/A",
];

export enum Sex {
  Male = "MALE",
  Female = "FEMALE",
}

export type InsuranceStatus = {
  error: string | null;
  memberId: string;
  network: "IN" | "OUT";
  payer: string;
  state: string;
  status: InsurancesStatusEnum;
  hasBenefits: boolean;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  otherLikelyInsurances?: any; // passthrough of json object, get schema
  pcpName?: string;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  subscriberInfo?: any; // passthrough of json object, get schema
  planName?: string;
  planStartDate?: string;
  planEndDate?: string;
  lastCheckedAt?: Date;
};

/** For use when retrieving insured prices */
export const loadingPhrases = [
  "Synchronizing data.",
  "Parsing benefits.",
  "Reading plan info.",
  "Checking deductible.",
  "Checking out-of-pocket.",
  "Calculating price.",
  "Checking exceptions.",
  "Finalizing.",
];

export enum InsuranceMode {
  INSURANCE = "INSURANCE",
  SELFPAY = "SELFPAY",
}

export enum RequestSource {
  COMPANION = "COMPANION",
  STOREFRONT = "STOREFRONT",
  ACCOUNT = "ACCOUNT",
  KIOSK = "KIOSK",
}

export enum RequestType {
  CHECK_INSURANCE_STATUS = "CHECK_INSURANCE_STATUS",
  REFRESH_STATUS = "REFRESH_STATUS",
  RUN_CHECK = "RUN_CHECK",
  REFRESH_MONTHLY_STATUS = "REFRESH_MONTHLY_STATUS",
}

export enum PaymentType {
  CASH = "CASH",
  INSURED = "INSURED",
  OVERRIDE_INSURED = "OVERRIDE_INSURED",
  OVERRIDE_SELFPAY = "OVERRIDE_SELFPAY",
}

export interface PricedTreatment {
  id: number;
  hrtId: number;
  price: number;
  copayUsed: boolean;
  paymentType: PaymentType;
  metadata: PricingMetadata;
}

export const numberInputSchema = z
  .union([z.string(), z.number()])
  .nullable()
  .optional()
  .transform((val) => {
    if (val === "" || val === null || val === undefined) {
      return null;
    }

    return Number(val);
  });

export const dateInputSchema = z
  .string()
  .nullable()
  .optional()
  .transform((val) => {
    if (val === "" || val === null || val === undefined) {
      return null;
    }

    try {
      const parsedDate = parse(val.slice(0, 10), "yyyy-MM-dd", new Date());

      // Check if date is valid
      if (Number.isNaN(parsedDate.getTime())) {
        return null;
      }

      return format(parsedDate, "yyyy-MM-dd");
    } catch (error) {
      console.error("Error parsing date:", error);
      return null;
    }
  });

export const copaySchema = numberInputSchema
  .refine((val) => val === null || val >= 0, {
    message: "Copay must be a positive number",
  })
  .refine((val) => val === null || val <= 1000, {
    message: "Copay must be less than or equal to $1000",
  });

export const coinsuranceSchema = numberInputSchema
  .refine((val) => val === null || val >= 0, {
    message: "Coinsurance must be a positive %",
  })
  .refine((val) => val === null || val <= 100, {
    message: "Coinsurance must be less than or equal to 100%",
  });

export const planStartDateSchema = dateInputSchema;

// make sure plan end date is after plan start date
export const planEndDateSchema = dateInputSchema.refine(
  (val) => {
    if (val === null) {
      return true;
    }

    try {
      parse(val, "yyyy-MM-dd", new Date());
      return true;
    } catch (error) {
      return false;
    }
  },
  {
    message: "Date format is invalid",
  },
);
// .refine(validateDateIsTodayOrFuture, {
//   message: "Plan end must be today or in the future",
// })

export const providerSchema = z.object({
  id: z.number(),
  npiId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  ehrId: z.string().nullish(),
  inNetwork: z.boolean().optional(),
});

export const procedureSchema = z.object({
  id: z.number(),
  hrtId: z.number(),
  name: z.string(),
  displayName: z.string(),
  description: z.string().nullish(),
  isActive: z.boolean().optional(),
});

export const cartItemSchema = z.object({
  id: z.number(),
  paymentType: z.enum([
    "INSURED",
    "CASH",
    "OVERRIDE_INSURED",
    "OVERRIDE_SELFPAY",
  ]),
  price: z.number(),
  hrts: z.array(procedureSchema),
});

export const pricedTreatmentSchema = z.object({
  id: z.number(),
  paymentType: z.enum([
    "INSURED",
    "CASH",
    "OVERRIDE_INSURED",
    "OVERRIDE_SELFPAY",
  ]),
  price: z.number(),
  hrtsId: z.number(),
  copayUsed: z.boolean(),
});

export const payerSchema = z.object({
  id: z.number(),
  payerName: z.string(),
  availity_payer_code: z.string().nullable(),
  pverify_payer_code: z.string().nullable(),
  ribbon_carrier_name: z.string(),
  athena_health_payer_name: z.string(),
});

/**
 * For use on the ENCOUNTER LEVEL. Meant for use with actual encounters.
 */
export const insuranceSchema = z.object({
  memberId: z.string(),
  state: z.string(),
  payer: z.string(),
  isPrimary: z.boolean().optional(), // Denotes if this insurance is the corresponding user's primaryInsurance
  isPrimaryInsurance: z.boolean().optional(), // THIS IS ONLY USED ON ENCOUNTER CREATION
  consumerId: z.string().nullable().optional(),
  id: z.string().uuid(),
  planName: z.string().nullable().optional(),
  planStartDate: planStartDateSchema.nullable().optional(),
  planEndDate: planEndDateSchema.nullable().optional(),
  insuranceAccountStatus: z.nativeEnum(InsuranceAccountStatus),
  status: z.nativeEnum(InsurancesStatusEnum).optional(),
  insuredFirstName: z.string().nullable().optional(),
  insuredLastName: z.string().nullable().optional(),
  priority: z.number().nullable().optional(),
  subscriberInfo: z.object({}).passthrough().nullable().optional(), // could add schema but alex said just passthrough (?)
  pcpName: z.string().nullable().optional(),
  otherInsurances: z.object({}).passthrough().nullable().optional(),
  lastCheckedAt: z.string().nullable().optional(),
  lastUpdatedBy: z.string().nullable().optional(),
  pcpCopayAmount: copaySchema.nullable().optional(),
  specialistCopayAmount: copaySchema.nullable().optional(),
  pcpCoinsurancePercentage: coinsuranceSchema.nullable().optional(),
  specialistCoinsurancePercentage: coinsuranceSchema.nullable().optional(),
});

export type InsuranceSchema = z.infer<typeof insuranceSchema>;

export const manualOverrideSchema = z.object({
  planStartDate: planStartDateSchema.nullable().optional(),
  planEndDate: planEndDateSchema.nullable().optional(),
  pcpCopayAmount: copaySchema.nullable().optional(),
  specialistCopayAmount: copaySchema.nullable().optional(),
  pcpCoinsurancePercentage: coinsuranceSchema.nullable().optional(),
  specialistCoinsurancePercentage: coinsuranceSchema.nullable().optional(),
});

export const secondaryManualOverrideSchema = manualOverrideSchema.extend({
  planStartDate: planStartDateSchema.nullable().optional(),
});

export const simplifiedInsuranceSchema = z.object({
  primaryInsurance: manualOverrideSchema,
  secondaryInsurance: secondaryManualOverrideSchema.optional(),
});

/**
 * For use on the FORM LEVEL. For use within createFormSchema. NOT MEANT FOR USE WITH ACTUAL ENCOUNTERS (see insuranceSchema)
 */
export const formInsuranceSchema = z.object({
  id: z.string().optional(),
  memberId: z.string().trim().min(1, "Please enter a valid member ID."),
  state: z.string().min(1, "Please enter valid state initials."),
  payer: z.string().min(1, "Please enter a valid payer"),
  isPrimaryInsurance: z.boolean().optional(),
  markedForDeletion: z.boolean().optional(),
  status: z.nativeEnum(InsurancesStatusEnum).optional(),
  // used to keep track of real time results to be used for PC specific flows
  hasBenefits: z.boolean().optional(),
  inNetwork: z.boolean().optional(),
  planName: z.string().nullable().optional(),
  planStartDate: planStartDateSchema.nullable().optional(),
  planEndDate: planEndDateSchema.nullable().optional(),
  insuredFirstName: z.string().nullable().optional(),
  insuredLastName: z.string().nullable().optional(),
  priority: z.number().nullable().optional(),
  subscriberInfo: z.object({}).passthrough().nullable().optional(), // could add schema but alex said just passthrough (?)
  pcpName: z.string().nullable().optional(),
  otherInsurances: z.object({}).passthrough().nullable().optional(),
  lastCheckedAt: z.string().nullable().optional(),
  lastUpdatedBy: z.string().nullable().optional(),
  pcpCopayAmount: copaySchema.nullable().optional(),
  specialistCopayAmount: copaySchema.nullable().optional(),
  pcpCoinsurancePercentage: coinsuranceSchema.nullable().optional(),
  specialistCoinsurancePercentage: coinsuranceSchema.nullable().optional(),
});

export const formInsuranceStorefrontSchema = z.object({
  hipaa: z.boolean().refine((checked) => checked === true),
  insurances: z.array(formInsuranceSchema),
});

export const retrievalApprovedSchema = z.object({ isApproved: z.boolean() });

export const validatePhoneNumber = (val: string) => {
  // Check that the string only contains digits, spaces, parentheses, hyphens, and plus signs
  const noLettersRegex = /^[0-9\s()+\-]+$/;
  return noLettersRegex.test(val);
};

// FIXME: Need to validate these but validator we're using only works behind a client boundary, find a new one.
export const phoneSchema = z.object({
  phone: z.string().refine(validatePhoneNumber, {
    message: "Please enter a valid phone number",
  }),
});

/**
 * Payment contact on the FORM level. For use within the apptFormSchema. NOT meant for use when fetching encounters (see paymentContactSchema).
 */
export const formPaymentContactSchema = z
  .object({
    fname: z
      .string()
      .min(1, "Please enter a payment contact first name.")
      .max(50, "Please enter at most 50 characters."),
    lname: z
      .string()
      .min(1, "Please enter a payment contact last name.")
      .max(500, "Please enter at most 500 characters."),
    email: z
      .string()
      .email("Please enter a valid payment contact email.")
      .or(z.literal("")),
  })
  .merge(phoneSchema);

export const sexSchema = z.object({
  sex: z.object(
    {
      label: z
        .string()
        .refine(
          (val) =>
            val.toLocaleLowerCase() === "male" ||
            val.toLocaleLowerCase() === "female" ||
            val.toLocaleLowerCase() === "prefer not to say",
          {
            message: "Please select a sex",
          },
        ),
      id: z.number(),
    },
    {
      errorMap: (issue, _ctx) => {
        switch (issue.code) {
          case "invalid_type":
            return { message: "Please select a sex" };
          default:
            return { message: "Please select a sex" };
        }
      },
    },
  ),
});

/**
 * Duplicating schema until decisions are unblocked - this schema is for Sorin only
 *
 * Used for
 */
export const BasicInfoSchema = z
  .object({
    fname: z
      .string()
      .min(1, "Please enter a first name.")
      .max(50, "Please enter at most 50 characters."),
    lname: z
      .string()
      .min(1, "Please enter a last name.")
      .max(500, "Please enter at most 500 characters."),
    dob: z
      .string()
      .min(1, "Please enter a date of birth.")
      .refine(
        (data) =>
          compareAsc(parse(data, "yyyy-MM-dd", new Date()), new Date()) === -1,
        {
          message: "Date of birth must be before today.",
        },
      ),
    email: z
      .string()
      .email("Please enter a valid email.")
      .or(z.literal(""))
      .nullish(),
  })
  .merge(phoneSchema);

/**
 * The surgeon / procedure / anesthesia object should also have rates associated with them
 * https://github.com/colinhacks/zod/issues/310
 */
export const createFormSchemaFields = BasicInfoSchema.extend({
  paymentContact: z.optional(formPaymentContactSchema),
  provider: z
    .object({
      label: z.string(),
      id: z.string(),
    })
    .nullable()
    .refine((x) => x !== null, { message: "Required" }),
  location: z
    .object({
      label: z.string(),
      id: z.string(),
    })
    .nullable()
    .refine((x) => x !== null, { message: "Required" }),
  anesthesiaType: z.optional(
    z.object({
      label: z.string(),
      id: z.union([z.number(), z.string()]),
    }),
  ),
  appointmentDate: z
    .string()
    .min(1, { message: "Appointment date is required." }),
  appointmentStartTime: z
    .string()
    .min(1, { message: "Appointment start time is required." }),
  appointmentEndTime: z.optional(z.string()),
  price: z.number().nullable(),
  procedures: z
    .array(
      z.object({
        label: z.string(),
        id: z.string(),
      }),
    )
    .min(1, { message: "Enter at least one procedure." }),
  visitReason: z.string().optional(),
  insuranceIds: z.array(z.string()).nullable().optional(),
  cartItemIds: z.array(z.number()).nullable().optional(),
  consumerId: z.string().optional(),
  userId: z.string().optional(),
  ehrVisitIds: z.string().array().optional(),
  intakeForms: z
    .object({ label: z.string(), id: z.string() })
    .array()
    .optional(),
  customSlot: z.boolean().optional(),
  scheduleSlotName: z.string().optional(),
});

export const createFormSchema = z
  .discriminatedUnion("insuranceMode", [
    z
      .object({ insuranceMode: z.literal(InsuranceMode.SELFPAY) })
      .merge(createFormSchemaFields),
    z
      .object({ insuranceMode: z.literal(InsuranceMode.INSURANCE) })
      .extend({ primaryInsurance: formInsuranceSchema })
      .extend({ secondaryInsurance: z.optional(formInsuranceSchema) })
      .merge(createFormSchemaFields),
  ])
  .refine(
    (data) => {
      if (data.appointmentEndTime === undefined) {
        return true;
      }
      return (
        compareAsc(
          parse(data.appointmentStartTime, "HH:mm", new Date()),
          parse(data.appointmentEndTime, "HH:mm", new Date()),
        ) === -1
      );
    },
    {
      message: "Appointment end must be after start",
      path: ["appointmentEndTime"],
    },
  )
  .refine(
    (data) => {
      if (data.paymentContact) {
        // If there is a payment contact phone AND patient phone
        if (
          data.paymentContact.phone !== null &&
          data.paymentContact.phone.trim() !== "" &&
          data.phone !== null &&
          data.phone.trim() !== ""
        ) {
          return data.paymentContact.phone !== data.phone;
        }
        // If there is a payment contact email AND patient email
        if (
          data.paymentContact.email != null &&
          data.paymentContact.email.trim() !== "" &&
          data.email !== null &&
          typeof data.email !== "undefined" &&
          data.email.trim() !== ""
        ) {
          return data.paymentContact.email !== data.email;
        }
      }
      return true;
    },
    {
      message: "Payment contact cannot be the same information as the patient.",
      path: ["paymentContact.phone"],
    },
  )
  .refine(
    (data) => {
      // If there is no contact information, require one
      if (
        (data.phone === null || data.phone.trim() === "") &&
        (data.email === null ||
          typeof data.email === "undefined" ||
          data.email.trim() === "")
      ) {
        return false;
      }
      return true;
    },
    {
      message: "Please enter at least one form of contact information.",
      path: ["phone"],
    },
  )
  .refine(
    (data) => {
      if (data.paymentContact) {
        if (
          (data.paymentContact.email == null ||
            data.paymentContact.email.trim() === "") &&
          (data.paymentContact.phone === null ||
            data.paymentContact.phone.trim() === "")
        )
          return false;
      }
      return true;
    },
    {
      message:
        "Please enter at least one form of contact information for payment contact.",
      path: ["paymentContact.phone"],
    },
  );

export const userSchema = z.object({
  id: z.string().optional(),
  firstname: z.string(),
  lastname: z.string(),
  phone: z.string(),
  email: z.string().nullable(),
  secureFlowFlag: z.boolean().optional(),
  saveInfoFlag: z.boolean().optional(),
});

const addressSchema = z.object({
  streetAddress: z.string(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  country: z.string(),
});

const optionalAddressSchema = z.object({
  streetAddress: z.string().nullish().or(z.literal("")),
  city: z.string().nullish().or(z.literal("")),
  state: z.string().nullish().or(z.literal("")),
  zip: z.string().nullish().or(z.literal("")),
  country: z.string().nullish().or(z.literal("")),
});

function nullableObject<T>(schema: T extends ZodTypeAny ? T : never) {
  return z.preprocess((val) => {
    if (!val) {
      return null;
    }

    let objectsToTest: unknown[] = [val];
    let objectHasNonEmptyValues = false;

    while (!objectHasNonEmptyValues && objectsToTest.length > 0) {
      const obj = objectsToTest.pop() as object;
      const nonEmptyValues = [...Object.values(obj)].filter(
        (val) => val !== null && val !== undefined && val !== "",
      );

      for (const value of nonEmptyValues) {
        if (typeof value !== "object") {
          objectHasNonEmptyValues = true;
          break;
        }
      }

      if (objectHasNonEmptyValues) {
        break;
      }

      objectsToTest = [...objectsToTest, ...nonEmptyValues];
    }

    return objectHasNonEmptyValues ? (val as T) : null;
  }, schema);
}

const consumerSchema = z.object({
  id: z.string().optional(),
  firstname: z.string(),
  lastname: z.string(),
  dob: z.string(),
  userId: z.string().nullable().optional(),
  primaryInsuranceId: z.string().nullable().optional(),
  stripeCustomerId: z.string().nullable().optional(),
});

const chargeSchema = z.object({
  orgId: z.string(),
  type: z.enum(CHARGETYPE),
  total: z.number().nullable(),
  name: z.string().nullable(),
});

export const rosterTransactionSchema = z.object({
  userId: z.optional(z.union([z.string().uuid(), z.string().nullable()])),
  encounterId: z.optional(z.union([z.string().uuid(), z.string().nullable()])),
  transactionId: z.string().uuid(),
  status: transactionStatus,
  stripePaymentId: z.string().nullable(),
  price: z.number(),
  date: z.string(),
  paidDate: z.string().nullable(),
  transactionType: transactionType,
  paymentMethodType: paymentmethod.optional().nullable(),
  stripePaymentMethodId: z
    .string()
    .regex(paymentmethodIdRegex)
    .optional()
    .nullable(),
  charges: z.array(chargeSchema),
  cardinfo: z.string().optional().nullable(),
});

const paginationMetaSchema = z.object({
  page: z.number(),
  perPage: z.number(),
  itemCount: z.number(),
  pageCount: z.number(),
  hasPreviousPage: z.boolean(),
  hasNextPage: z.boolean(),
});

export const featureFlagsSchema = z.object({
  /** Org supports additional hrts (multi-hrt encounters) */
  additionals: z.boolean(),
  /** Org supports toggling hrts on and off */
  hrtToggle: z.boolean(),
  /** Org supports unscheduled home tab */
  unscheduled: z.boolean(),
  /** Org supports "arriving soon" status pill on encounter card */
  arrivingSoonStatus: z.boolean(),
  /** Org supports "needs provider" status pill on encounter card */
  needsProviderStatus: z.boolean(),
  /** Org supports walk in visits, "walk in" checkbox on create new */
  walkins: z.boolean(),
  /** Org does NOT require a base visit to be selected in order to choose additionals on create new */
  optionalBase: z.boolean(),
  /** Show consults on the storefront */
  storefrontConsults: z.boolean().optional(),
  /** Allow booking on the storefront */
  storefrontBooking: z.boolean().optional(),
  /**  Whether or not ceratin orgs can mark certain encounters */
  actionModal: z.boolean().optional(),
});

export const practiceSchema = z.object({
  practiceId: z.number(),
  address: z.string(),
  locationName: z.string(),
  googleMapsEmbed: z.string(),
  googleMapsUrl: z.string(),
  metadata: z.any(),
  orgId: z.number(),
  phoneNumber: z.string().nullable().optional(),
  ehrLocationId: z.string().nullable().optional(),
  isAvailableFromStorefront: z.boolean().nullable().optional(),
  timezone: z.string(),
});

export const orgStylesSchema = z.any();

/**
 * Deprecating this style schema
 */
// export const orgStylesSchema = z.object({
//   primary: z.string(),
//   secondary: z.string(),
//   background: z.string(),
//   fullLogoPath: z.string(),
//   textlessLogoPath: z.string(),
// });

export const organizationSchema = z.object({
  id: z.number(),
  orgName: z.string(),
  orgTitle: z.string(),
  ein: z.string(),
  metadata: z.any(),
  waitingRoomPin: z.string(),
  googleAnalyticsTag: z.string().nullable().optional(),
  facebookPixelId: z.string().nullable().optional(),
  practices: z.array(practiceSchema),
  schedulingTimeWindow: z.number().optional(),
  schedulingType: z.enum(["EHR", "AGNOSTIC", "GENERIC_ONLY"]).optional(),
  supportedLanguages: z.array(z.string()).optional(),
});

export const orgConfigSchema = z.object({
  organization: organizationSchema,
  practice: practiceSchema.nullable(),
  styles: orgStylesSchema,
});

const hasFourDigitYear = (data: string) => {
  const year = data.substring(data.length - 4);
  return /^\d{4}$/.test(year) && Number.parseInt(year) >= 1000;
};

export const newPatientSchema = z
  .object({
    fname: z
      .string()
      .min(1, "Please enter a first name.")
      .max(50, "Please enter at most 50 characters."),
    lname: z
      .string()
      .min(1, "Please enter a last name.")
      .max(500, "Please enter at most 500 characters."),
    dob: z
      .string()
      .min(1, "Please enter a date of birth.")
      .refine((data) => hasFourDigitYear(data), {
        message: "Date of birth must match the format MM/dd/yyyy.",
      })
      .refine(
        (data) =>
          isAfter(
            parse(data, "MM/dd/yyyy", new Date()),
            parse("1900-01-01", "yyyy-MM-dd", new Date()),
          ),
        {
          message: "Date of birth must be after 01/01/1900.",
        },
      )
      .refine(
        (data) => isBefore(parse(data, "MM/dd/yyyy", new Date()), new Date()),
        {
          message: "Date of birth must be before today.",
        },
      ),
    email: z
      .string()
      .email("Please enter a valid email.")
      .or(z.literal(""))
      .nullish(),
    primaryLanguage: z.nativeEnum(LanguageCode),
    consumerFirstName: z.string().min(1, "Please enter a first name."),
    preferredFirstName: z.string().nullish(),
    consumerLastName: z.string().min(1, "Please enter a last name."),
    preferredLastName: z.string().nullish(),
    sex: z
      .string()
      .refine(
        (val) =>
          !val ||
          val.toLowerCase() === "male" ||
          val.toLowerCase() === "female",
        {
          message: "Please select a sex",
        },
      ),
    primaryInsurance: formInsuranceSchema.optional(),
    secondaryInsurance: formInsuranceSchema.optional(),
    tertiaryInsurance: formInsuranceSchema.optional(),
  })
  .merge(addressSchema)
  .merge(phoneSchema);

const optionalPhoneSchema = z.object({
  phone: z.string().optional().nullable(),
});

export const patientAdditionalInfoSchema = z
  .object({
    dob: z
      .string()
      .min(1, "Please enter a date of birth.")
      .refine((data) => isValid(parse(data, "MM/dd/yyyy", new Date())), {
        message: "Enter DOB in MM/dd/yyyy format",
      })
      .refine(
        (data) => isBefore(parse(data, "MM/dd/yyyy", new Date()), new Date()),
        {
          message: "Date of birth must be before today.",
        },
      ),
    email: z
      .string()
      .email("Please enter a valid email.")
      .or(z.literal(""))
      .nullish(),
    primaryLanguage: z.nativeEnum(LanguageCode),
    consumerFirstName: z.string().min(1, "Please enter a first name."),
    preferredFirstName: z.string().nullish(),
    consumerLastName: z.string().min(1, "Please enter a last name."),
    preferredLastName: z.string().nullish(),
    primaryInsurance: formInsuranceSchema.optional(),
    secondaryInsurance: formInsuranceSchema.optional(),
    tertiaryInsurance: formInsuranceSchema.optional(),
    sex: z
      .string()
      .refine(
        (val) =>
          !val ||
          val.toLowerCase() === "male" ||
          val.toLowerCase() === "female",
        {
          message: "Please select a sex",
        },
      ),
    maritalStatus: z.nativeEnum(MaritalStatus).nullish(),
    race: z.nativeEnum(Race).array(),
    ethnicity: z.nativeEnum(Ethnicity).nullish(),
    pharmacy: nullableObject(
      z
        .object({
          name: z.string().min(1, "Required"),
          address: optionalAddressSchema.nullish(),
          ehrId: z.string().nullish(),
        })
        .merge(optionalPhoneSchema)
        .nullish(),
    ),
    pcp: nullableObject(
      z
        .object({
          firstName: z.string().min(1, "Required"),
          lastName: z.string().min(1, "Required"),
          address: optionalAddressSchema.nullish(),
          ehrId: z.string().nullish(),
        })
        .merge(optionalPhoneSchema)
        .nullable(),
    ),
    emergencyContact: nullableObject(
      z
        .object({
          firstName: z.string().min(1, "Required"),
          lastName: z.string().min(1, "Required"),
          relationship: z.nativeEnum(EmergencyContactRelationship),
        })
        .merge(phoneSchema)
        .nullable(),
    ),
    referralSource: nullableObject(
      z
        .object({
          option: z.string().min(1, "Required"),
          id: z.string(),
          detail: z.string().nullish(),
        })
        .nullable(),
    ),
  })
  .merge(addressSchema)
  .merge(phoneSchema);

export type PhoneData = z.infer<typeof phoneSchema>;

export type BasicInfoData = z.infer<typeof BasicInfoSchema>;

export type PaymentContactData = z.infer<typeof formPaymentContactSchema>;

export type CreateFormData = z.infer<typeof createFormSchema>;

export type InsuranceData = z.infer<typeof insuranceSchema>;
export type FormInsuranceData = z.infer<typeof formInsuranceSchema>;

export type PayerData = z.infer<typeof payerSchema>;
export type UserData = z.infer<typeof userSchema>;
export type ProviderData = z.infer<typeof providerSchema>;
export type ProcedureData = z.infer<typeof procedureSchema>;
export type CartItemData = z.infer<typeof cartItemSchema>;
export type PricedTreatmentData = z.infer<typeof pricedTreatmentSchema>;
export type PracticeData = z.infer<typeof practiceSchema>;
export type OrganizationData = z.infer<typeof organizationSchema>;
export type ManualOverrideData = z.infer<typeof manualOverrideSchema>;

export type Address = z.infer<typeof addressSchema>;

export type OrgStylesData = z.infer<typeof orgStylesSchema>;
export type OrgConfigData = z.infer<typeof orgConfigSchema>;
export type FeatureFlagsData = z.infer<typeof featureFlagsSchema>;
export type ConsumerData = z.infer<typeof consumerSchema>;
export type TransactionData = z.infer<typeof rosterTransactionSchema>;
export type NewPatientData = z.infer<typeof newPatientSchema>;
export type AdditionalPatientData = z.infer<typeof patientAdditionalInfoSchema>;
export type SimplifiedInsuranceType = z.infer<typeof simplifiedInsuranceSchema>;
